import storageService from '../../services/storage';
import couponService from '../../services/coupon';
import glp1TreatmentService from '../../services/glp1-treatment';

/**
 * Sets the treatment type in the local storage based on the hidden input when having multiple medications slider
 */
const setTreatmentType = () => {
  storageService().treatmentType = document.querySelector('#treatment_type').value;
};

/**
 * Sets whether shipping is required in the local storage based on the hidden input when having multiple medications
 * slider
 */
const setIsShippingRequired = () => {
  storageService().isShipped = document.querySelector('#requires_shipping').value;
};

/**
 * Helper function to add a value to the local storage
 *
 * @param {string} key   the key to store the value
 * @param {value}  value the value to store
 */
const addStorageValue = (key, value) => {
  const storage = storageService()[key];
  storage.push(value);
  storageService()[key] = storage;
};

/**
 * Adds multiple upsells to the local storage
 *
 * @param {object[]} upsells array of upsells to add to the local storage
 */
const addUpsells = (upsells) =>
  upsells.forEach((upsell) =>
    addStorageValue('orderUpsells', {
      id: parseInt(upsell.id),
      name: upsell.name,
      slug: upsell.slug,
      price: parseInt(upsell.price),
      upsell_price: parseInt(upsell.upsellPrice),
    }),
  );

/**
 * Adds the extra charge to the local storage if the price is greater than 0 or the flag is set to true
 *
 * @param {object}  answer    the extra charge option to add to the local storage
 */
const addExtraCharge = (answer) => {
  if (parseInt(answer.extraCharge) <= 0) {
    return;
  }

  addStorageValue('extraCharges', { name: answer.name, amount: parseInt(answer.extraCharge), type: 'Extra Charge' });
};

/**
 * Merges multiple answers combining their names, slugs and summing their prices and extra charges.
 *
 * @param {object[]} answers array of answer objects
 */
const mergeAnswers = (answers) => {
  const slug = answers.map((a) => a.slug).join('-');
  const slugMapEntry = window.slugMap?.[slug];
  const id = answers.length === 1 && answers[0].id ? answers[0].id : slugMapEntry?.id;
  const planId = answers.length === 1 && answers[0].plan_id ? answers[0].plan_id : slugMapEntry?.planId;

  return {
    name: [...new Set(answers.map((a) => a.name))].join(' - '),
    slug,
    price: answers.reduce((sum, a) => sum + parseInt(a.price), 0),
    extraCharge: answers.reduce((sum, a) => sum + parseInt(a.extraCharge), 0),
    ...(id && { id: parseInt(id) }),
    ...(planId && { planId }),
  };
};

/**
 * Adds the test to the local storage and adds the extra charge when applicable
 *
 * @param {object} answer the test option to add to the local storage
 */
const addTest = (answer) => {
  addStorageValue('tests', {
    name: answer.name,
    slug: answer.slug,
    price: answer.price,
    id: answer.id,
    ...(answer.planId && { plan_id: answer.planId }),
  });
};

/**
 * Gets slider answers of a specific type from storage.
 *
 * @param {'Test'|'Upsell'} type the type of answers to filter for
 */
const getAnswersByType = (type) => {
  return Object.values(storageService().sliderAnswers).filter((answer) => answer.type === type);
};

/**
 * Event listener for the multiple medications slider callback event
 */
document.addEventListener('MultipleTestsCallback', () => {
  const testAnswer = mergeAnswers(getAnswersByType('Test'));

  storageService().clearCheckoutData();
  setTreatmentType();
  setIsShippingRequired();
  addTest(testAnswer);
  addExtraCharge(testAnswer);
  addUpsells(getAnswersByType('Upsell').filter((upsell) => parseInt(upsell.price) > 0));
  couponService().applyDefaultCoupon();
  storageService().sliderAnswers = {};

  window.location.href = glp1TreatmentService().isGlp1TreatmentType() ? '/order' : '/select-pharmacy';
});
